import * as Tooltip from '@radix-ui/react-tooltip';
import { useFlags } from 'launchdarkly-react-client-sdk';
import type { LaunchDarklyFlags } from '../../types/launch-darkly';
import styles from './AgentToolList.module.css';

const VISIBLE_TOOL_LIMIT = 3;

const TOOL_LIST_COPY = {
  none: '—',
} as const;

interface AgentToolListProps {
  tools: string[];
}

export const AgentToolList = ({ tools }: AgentToolListProps) => {
  const flags = useFlags<LaunchDarklyFlags>();
  const templatesAreEnabled = flags['enable-partner-agent-templates'] === true;
  const sortedTools = [...tools].sort((left, right) => left.localeCompare(right));
  const visibleToolLimit = templatesAreEnabled ? VISIBLE_TOOL_LIMIT : sortedTools.length;
  const visibleTools = sortedTools.slice(0, visibleToolLimit);
  const hiddenTools = sortedTools.slice(visibleToolLimit);
  const hasTools = sortedTools.length > 0;
  const hasHiddenTools = hiddenTools.length > 0;
  const overflowLabel = `+${hiddenTools.length}`;
  const overflowDescription = `${hiddenTools.length} more tools`;

  if (!hasTools) {
    return <span className={styles.none}>{TOOL_LIST_COPY.none}</span>;
  }

  return (
    <ul className={styles.tools} data-templates-enabled={templatesAreEnabled}>
      {visibleTools.map(tool => (
        <li key={tool} className={styles.tool}>
          {tool}
        </li>
      ))}
      {hasHiddenTools && (
        <li>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <button type="button" className={styles.overflow} aria-label={overflowDescription}>
                {overflowLabel}
              </button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content side="top" sideOffset={6} className={styles.tooltip}>
                <ul>
                  {hiddenTools.map(tool => (
                    <li key={tool}>{tool}</li>
                  ))}
                </ul>
                <Tooltip.Arrow className={styles.arrow} />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </li>
      )}
    </ul>
  );
};
